import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import RefreshTimer from "../../util/RefreshTimer";
import {LidarOverviewContext} from "./context/LidarOverviewContext";
import Row from "@meridian/components/row";
import {
    getAssetRefreshInterval
} from "../../util/Util";
import Column from "@amzn/meridian/column";
import {FormattedMessage, injectIntl} from "react-intl";
import {
    ACTIVE,
    BLUE_COLOR, INACTIVE, ONLINE_OK,
    ORANGE_COLOR, THREE_DOTS,
} from "../../util/Constants";
import Button from "@amzn/meridian/button";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Icon from "@amzn/meridian/icon";
import Box from "@amzn/meridian/box";
import Tooltip from "@amzn/meridian/tooltip";
import Loader from "../../loader/Loader";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import Alert from "@amzn/meridian/alert";
import LidarControllerBanner from "../thinclient/banner/LidarControllerBanner";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import AddEditSingleLidarControllerModal from "./modal/AddEditSingleLidarControllerModal";
import Text from "@amzn/meridian/text";
import Menu, {MenuItem} from "@amzn/meridian/menu";
import LidarConfigurationModal from "./modal/LidarConfigurationModal";
import DeleteSingleDeviceModal from "../../commonui/DeleteSingleDeviceModal";
import UpdateDeviceStatusModal from "../../commonui/UpdateDeviceStatusModal";
import ManageObstructionModal from "./modal/ManageObstructionModal";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";

const LidarOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(LidarOverviewContext);
    const {showCustomAlert} = useContext(GlobalStateContext);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openManageObstructionModal, setOpenManageObstructionModal] = useState(false);
    const [openConfigModal, setOpenConfigModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState(false)
    const [enableObstruction, setEnableObstruction] = useState(false);
    const [status, setStatus] = useState("")
    const [deviceLocation, setDeviceLocation] = useState("")
    const tableEndRef = useRef(null);
    const actionButtonRefs = useRef([]);
    const [openActionsMenu, setOpenActionsMenu] = useState({});
    const onClickActionsButton = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = true;
        setOpenActionsMenu(_open);
    }, []);
    const onCloseActions = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = false;
        setOpenActionsMenu(_open)
    }, []);

    useEffect(() => {
        if (!openAddModal && !openDeleteModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal, openDeleteModal]);
    useEffect(() => {
        actionButtonRefs.current = actionButtonRefs.current.slice(0, viewModel.lidarDeviceInformation.length);
    }, [viewModel.lidarDeviceInformation]);

    const onSort = (sortDetails) => {
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        })
    }
    const sortedDeviceList = Object.values(viewModel.lidarDeviceInformation);
    sortedDeviceList && sortedDeviceList.sort((a, b) => {
        if (a[viewModel.sortPref.column] < b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? -1 : 1
        }
        if (a[viewModel.sortPref.column] > b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? 1 : -1
        }
        return 0
    })

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const getData = () => {
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const onClickAddEditDevice = () => {
        setSelected(null);
        setOpenAddModal(true);
    }

    const onClickEditDevice = (mac) => {
        setSelected(mac);
        setOpenAddModal(true);
    }

    const onClickViewConfiguration = (mac) => {
        setSelected(mac);
        setOpenConfigModal(true);
    }

    const onClickDisableObstruction = (mac, obstructionDisabledUntil) => {

        if (obstructionDisabledUntil && obstructionDisabledUntil > Date.now()) {
            showCustomAlert(`Obstruction is already disabled until ${new Date(obstructionDisabledUntil).toUTCString()}`);
        } else {
            setSelected(mac);
            setEnableObstruction(false);
            setOpenManageObstructionModal(true);
        }
    }

    const onClickEnableObstruction = (mac) => {
            setSelected(mac);
            setEnableObstruction(true)
            setOpenManageObstructionModal(true);
    }

    const onClickDeactivate = (mac, location) => {
        setSelected(mac);
        setStatus(INACTIVE);
        setDeviceLocation(location);
        setOpenUpdateStatusModal(true);
    }

    const onClickActivate = (mac, location) => {
        setSelected(mac);
        setStatus(ACTIVE);
        setDeviceLocation(location);
        setOpenUpdateStatusModal(true);
    }

    const onClickDeleteDevice = (mac) => {
        setSelected(mac);
        setOpenDeleteModal(true);
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Row widths="100%"
                 alignmentVertical="top"
                 spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Box></Box>
                        <Tooltip position="top" title={intl.formatMessage({
                            id: 'tooltip.scrolltobottom',
                            defaultMessage: 'Scroll to bottom!'
                        })}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    {sortedDeviceList.length === 0 ?
                        <Alert
                            type="informational"
                            size="small"
                        >
                            <FormattedMessage id="alert.device"
                                              defaultMessage="No device present for this station."/>
                        </Alert> :
                        <Table
                            headerRows={1}
                            spacing="small"
                            sortColumn={viewModel.sortPref.column}
                            sortDirection={viewModel.sortPref.direction}
                            onSort={onSort}
                            showDividers={true}
                            showStripes={true}>
                            <TableRow highlightOnHover={false}>
                                <TableCell sortColumn="mac">
                                    <FormattedMessage id="device.mac_ip" defaultMessage="MAC/IP"/>
                                </TableCell>
                                <TableCell sortColumn="connectivity">
                                    <FormattedMessage id="device.connectivity" defaultMessage="Connection Status"/>
                                </TableCell>
                                <TableCell sortColumn="location">
                                    <FormattedMessage id="device.location" defaultMessage="Locations"/>
                                </TableCell>
                                <TableCell sortColumn="status">
                                    <FormattedMessage id="device.status" defaultMessage="Status"/>
                                </TableCell>
                                <TableCell sortColumn="controller_type">
                                    <FormattedMessage id="device.controller_type" defaultMessage="Controller Type"/>
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="device.action" defaultMessage="Action"/>
                                </TableCell>
                            </TableRow>
                            <LidarControllerBanner/>
                            {sortedDeviceList.map((device, index) => (
                                <TableRow key={device.mac}
                                          highlightOnHover={true}>
                                    <TableCell> <b>{device.mac}</b>
                                        <br></br>
                                        {device.ip}
                                    </TableCell>
                                    <TableCell> <b style={{color: device.connectivity.toLowerCase() === ONLINE_OK ? BLUE_COLOR : ORANGE_COLOR}}>{device.connectivity}</b>
                                        <br></br>
                                        {device.timestamp}
                                    </TableCell>
                                    <TableCell>
                                        <b>{device.location}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{device.status}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{device.lidarControllerType}</b>
                                    </TableCell>
                                    <TableCell>
                                        <Text type="b300">
                                            <Button ref={el => actionButtonRefs.current[index] = el}
                                                               type="icon"
                                                               size="medium"
                                                               onClick={() => onClickActionsButton(index)}
                                                               aria-controls="my-menu"
                                                               aria-haspopup="menu"
                                                               aria-expanded="false"
                                                >
                                                    {THREE_DOTS}
                                                </Button>
                                            <Menu anchorNode={actionButtonRefs.current[index]}
                                                  open={openActionsMenu[index] ? openActionsMenu[index] : false}
                                                  position="left"
                                                  onClose={() => onCloseActions(index)}>
                                                <MenuItem onClick={() => onClickEditDevice(device.mac)}>
                                                    <FormattedMessage id="actions.addDevice"
                                                                      defaultMessage="Edit Device"/>
                                                </MenuItem>
                                                <MenuItem onClick={() => onClickViewConfiguration(device.mac)}>
                                                    <FormattedMessage id="actions.viewConfiguration"
                                                                      defaultMessage="View Configuration"/>
                                                </MenuItem>
                                                {(device.status === ACTIVE) ?
                                                    <MenuItem onClick={() => onClickDeactivate(device.mac, device.location)}>
                                                        <FormattedMessage id="actions.deactivate"
                                                                          defaultMessage="Deactivate"/>
                                                    </MenuItem> : <MenuItem
                                                        onClick={() => onClickActivate(device.mac, device.location)}>
                                                        <FormattedMessage id="actions.activate"
                                                                          defaultMessage="Activate"/>
                                                    </MenuItem>}
                                                {device.obstructionDisabledUntil !== null && device.obstructionDisabledUntil <= Date.now() ?
                                                <MenuItem onClick={() => onClickDisableObstruction(device.mac, device.obstructionDisabledUntil)}>
                                                    <FormattedMessage id="actions.disableObstruction"
                                                                      defaultMessage="Disable Obstruction"/>
                                                </MenuItem> : <MenuItem onClick={() => onClickEnableObstruction(device.mac)}>
                                                        <FormattedMessage id="actions.enableObstruction"
                                                                          defaultMessage="Enable Obstruction"/>
                                                    </MenuItem>}
                                                <MenuItem onClick={() => onClickDeleteDevice(device.mac)}>
                                                    <FormattedMessage id="button.delete" defaultMessage="Delete device"/>
                                                </MenuItem>
                                            </Menu>
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>}
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <Button onClick={onClickAddEditDevice} type="primary" size="small">
                                <Icon tokens={plusCircleTokens}/>
                                <FormattedMessage id="device.add_device_button_modal" defaultMessage="Add/Update Single Device Configuration"/>
                            </Button>
                    </Row>
                    <AddEditSingleLidarControllerModal open={openAddModal} setOpen={setOpenAddModal}
                                              selected={selected} setSelected={setSelected}/>
                    <ManageObstructionModal open={openManageObstructionModal}
                                             setOpen={setOpenManageObstructionModal}
                                                       selected={selected} setSelected={setSelected}
                                                enableObstruction={enableObstruction}/>
                    <LidarConfigurationModal open={openConfigModal} setOpen={setOpenConfigModal}
                                                   selected={selected} setSelected={setSelected}/>
                    <DeleteSingleDeviceModal open={openDeleteModal} setOpen={setOpenDeleteModal}
                                             selected={selected} setSelected={setSelected}
                                             macAddressArray={viewModel.macAddressArray} dispatch={dispatch}/>
                    <UpdateDeviceStatusModal open={openUpdateStatusModal} setOpen={setOpenUpdateStatusModal}
                                             selected={selected} setSelected={setSelected}
                                             status={status}
                                             dispatch={dispatch}
                                             deviceLocation={deviceLocation}
                                             additionalConfirmationMessage={status === ACTIVE ? intl.formatMessage({id: "modal.lidar.activate.message", defaultMessage: "Caution: Confirm that the last sensor in each aisle has been validated with the LiDAR tester before enabling Scanless. Enabling without validation will cause incorrect sensor-to-column mapping, leading to false obstructions and stow detection failures."}) : null}/>
                    <div ref={tableEndRef}/>
                </Column>
            </Row>
        );
    }
}

export default injectIntl(LidarOverview)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/LidarOverview.js